import { useMutation } from '@tanstack/react-query';
import { finishPayment } from 'api';
import { Loading, ModalPaymentSuccess } from 'components';
import { routes } from '../../../constants';
import { useMessage, useToast } from 'hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EPaymentMethod, TFinishPayment } from 'types';
import { useAppStore, useChatStore } from 'store';
import { usePubNub } from 'pubnub-react';

function FinishPayment() {
  // Hooks
  const { id: pendingPaymentId } = useParams();
  const { toastError } = useToast();
  const navigate = useNavigate();
  const { paymentPending } = useAppStore();
  const isFirst = useRef(true);
  const [searchParams] = useSearchParams();
  const pubnub = usePubNub();
  const { sendMessage } = useMessage({ pubnub });
  const { setShowReview, setStateReview } = useChatStore();

  const redirect = useMemo(
    () => decodeURIComponent(searchParams.get('redirect') || ''),
    [searchParams]
  );

  // States
  const [open, setOpen] = useState<boolean>(false);

  // Mutation
  const { mutateAsync, data: resFinishPayment } = useMutation({
    mutationFn: (body: TFinishPayment) => {
      return finishPayment(body);
    },
    onSuccess() {
      setOpen(true);
    },
  });

  // Callbacks
  const handleFinishPayment = useCallback(async () => {
    try {
      const res = await mutateAsync({ paymentId: pendingPaymentId || '' });
      if (res?.name && res?.channel) {
        sendMessage({
          name: res?.name,
          image: res?.image,
          animations: res?.animations,
          message: '',
          channel: res?.channel,
          female: res?.female,
        });
      }
    } catch (error: any) {
      toastError(error);
      navigate(routes.PAYMENT.replace(':id', pendingPaymentId || ''), {
        state: {
          channelId:
            error?.response?.data?.channelId ||
            resFinishPayment?.channelId ||
            '',
        },
      });
    }
  }, [
    mutateAsync,
    navigate,
    pendingPaymentId,
    resFinishPayment?.channelId,
    sendMessage,
    toastError,
  ]);

  const onConfirmSuccess = useCallback(() => {
    const finishState = {
      state: {
        isFinish: true,
      },
    };
    if (resFinishPayment?.isFinished) {
      if (resFinishPayment?.isPostPayment) {
        return navigate(routes.PREMIUM_PURCHASED, finishState);
      }
      navigate(redirect, finishState);
      setStateReview({ channelId: resFinishPayment?.channelId });
      setShowReview(true);
    } else {
      navigate(routes.PAYMENT.replace(':id', pendingPaymentId || ''), {
        state: { channelId: resFinishPayment?.channelId || '' },
      });
    }
  }, [
    resFinishPayment?.isFinished,
    resFinishPayment?.isPostPayment,
    resFinishPayment?.channelId,
    navigate,
    redirect,
    setStateReview,
    pendingPaymentId,
    setShowReview,
  ]);

  // Effect
  useEffect(() => {
    const listPaymentIds = paymentPending?.ids;
    const hasPaymentPending =
      pendingPaymentId && listPaymentIds?.includes(pendingPaymentId);
    if (!hasPaymentPending && listPaymentIds?.length) navigate(routes.SEARCH);
    if (isFirst.current && hasPaymentPending) {
      isFirst.current = false;
      handleFinishPayment();
    }
  }, [
    handleFinishPayment,
    navigate,
    paymentPending,
    paymentPending?.ids,
    pendingPaymentId,
  ]);

  return (
    <div className="h-full">
      <Loading center size="md" />
      <ModalPaymentSuccess
        open={open}
        paymentMethod={EPaymentMethod.VISA}
        onConfirm={onConfirmSuccess}
        amount={resFinishPayment?.amount || 0}
        text={resFinishPayment?.message}
      />
    </div>
  );
}

export default FinishPayment;
